import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import apiClient from '../api/client';

const AuthContext = createContext();

const DEV_USER = {
  id: 'dev-user-000',
  email: 'dev@ccai.local',
  username: 'dev_user',
  role: 'admin',
  created_at: new Date().toISOString(),
};

const isLocalhost = () => {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDevMode, setIsDevMode] = useState(false);

  const hydrateUser = useCallback(async (session) => {
    if (session?.access_token) {
      apiClient.setToken(session.access_token);
      const sbUser = session.user;

      // Role source of truth: Supabase `profiles` table (works without the API backend).
      let role = 'employee';
      try {
        const { data } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', sbUser?.id)
          .maybeSingle();
        if (data?.role) role = data.role;
      } catch {
        /* profiles table may not exist yet; fall back below */
      }

      const fallback = {
        id: sbUser?.id,
        email: sbUser?.email || '',
        username: sbUser?.user_metadata?.username || sbUser?.email?.split('@')[0] || '',
        role,
        created_at: sbUser?.created_at || new Date().toISOString(),
      };
      try {
        const profile = await apiClient.getCurrentUser();
        // Keep Supabase profiles role authoritative for access control.
        setUser({ ...profile, role });
      } catch {
        setUser(fallback);
      }
      setIsDevMode(false);
    } else {
      apiClient.setToken(null);
      if (isLocalhost()) {
        setUser(DEV_USER);
        setIsDevMode(true);
      } else {
        setUser(null);
        setIsDevMode(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!supabase) {
      if (isLocalhost()) {
        setUser(DEV_USER);
        setIsDevMode(true);
      }
      setLoading(false);
      return;
    }

    let mounted = true;

    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (mounted) {
        await hydrateUser(session);
        setLoading(false);
      }
    };
    init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        if (mounted) {
          await hydrateUser(session);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [hydrateUser]);

  const login = useCallback(async (email, password) => {
    if (!supabase) throw new Error('Supabase not configured');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  }, []);

  const signup = useCallback(async (email, username, password) => {
    if (!supabase) throw new Error('Supabase not configured');
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { username } },
    });
    if (error) throw error;
    return data;
  }, []);

  const logout = useCallback(async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    apiClient.setToken(null);
    if (isLocalhost()) {
      setUser(DEV_USER);
      setIsDevMode(true);
    } else {
      setUser(null);
      setIsDevMode(false);
    }
  }, []);

  const value = {
    user,
    loading,
    isDevMode,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
